import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";
import NewsletterForm from "@/components/NewsletterForm";

const footerLinks = {
  Shop: [
    { label: "All Books", href: "/products" },
    { label: "Alphabet Books", href: "/products" },
    { label: "Number Books", href: "/products" },
    { label: "Coloring Books", href: "/products" },
    { label: "Accessories", href: "/products" },
  ],
  Learn: [
    { label: "About Us", href: "/homepage" },
    { label: "Our Story", href: "/homepage" },
    { label: "For Educators", href: "/homepage" },
    { label: "Gift Cards", href: "/products" },
  ],
  Support: [
    { label: "FAQ", href: "/homepage" },
    { label: "Shipping Info", href: "/homepage" },
    { label: "Returns", href: "/homepage" },
    { label: "Contact Us", href: "/homepage" },
  ],
};

const countries = ["🇺🇸 USA", "🇬🇧 UK", "🇸🇬 Singapore", "🇨🇭 Switzerland", "🇦🇺 Australia", "🇳🇴 Norway", "🇩🇰 Denmark", "🇮🇪 Ireland", "🇱🇺 Luxembourg", "🇮🇸 Iceland"];

export default function Footer() {
  return (
    <footer className="bg-plum text-white/80 pt-16 pb-8 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full opacity-10 blur-3xl" style={{ background: "var(--lavender)" }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: "var(--peach)" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 md:gap-10 pb-12 border-b border-white/10">
          {/* Brand */}
          <div className="sm:col-span-2">
            <AppLogo size={64} text="My Happy Place Digital" iconName="BookOpenIcon" className="brightness-0 invert" />
            <p className="mt-4 text-sm text-white/60 leading-relaxed max-w-xs">
              Magical books that spark curiosity, creativity, and a lifelong love of learning — for children worldwide. 🌟
            </p>
            <div className="mt-5 flex flex-col gap-3 text-sm text-white/60">
              <a href="mailto:myhappyplacedigital@gmail.com" className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px]">
                <Icon name="EnvelopeIcon" size={16} />
                myhappyplacedigital@gmail.com
              </a>
              <a href="https://wa.me/256764590167" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px]">
                <Icon name="ChatBubbleLeftRightIcon" size={16} />
                +256 764 590 167 (WhatsApp)
              </a>
            </div>
            {/* Social */}
            <div className="mt-5 flex gap-3">
              <a
                href="https://www.instagram.com/myhappyplacedigital?utm_source=qr&igsh=MWM4dWtnc3VzamcyMA=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/myhappyplacedigital?utm_source=qr&igsh=MWM4dWtnc3VzamcyMA=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/myhappyplacedigital?utm_source=qr&igsh=MWM4dWtnc3VzamcyMA=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/myhappyplacedigital?utm_source=qr&igsh=MWM4dWtnc3VzamcyMA=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pinterest"
                className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks)?.map(([group, links]) => (
            <div key={group} className="sm:col-span-1">
              <h4 className="text-white font-bold text-xs tracking-widest uppercase mb-4">{group}</h4>
              <ul className="space-y-3">
                {links?.map((link) => (
                  <li key={link?.label}>
                    <Link href={link?.href} className="text-sm text-white/55 hover:text-white transition-colors inline-block min-h-[44px] flex items-center">
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Countries marquee */}
        <div className="py-6 border-b border-white/10 overflow-hidden">
          <p className="text-xs text-white/40 text-center mb-4 tracking-widest uppercase font-bold">Shipping Worldwide To</p>
          <div className="flex gap-4 sm:gap-6 flex-wrap justify-center">
            {countries?.map((c) => (
              <span key={c} className="text-xs sm:text-sm text-white/50 font-medium px-2 py-1">{c}</span>
            ))}
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="py-8 border-b border-white/10">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-white font-bold text-xl mb-2">✨ Join Our Newsletter</h3>
            <p className="text-white/60 text-sm mb-5">
              Get exclusive updates, new book releases, and special offers delivered to your inbox!
            </p>
            <NewsletterForm variant="footer" />
          </div>
        </div>

        {/* Row 2 — bottom */}
        <div className="pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40">
          <p className="text-center md:text-left">© 2026 My Happy Place Digital. All rights reserved.</p>
          <div className="flex flex-wrap gap-4 sm:gap-6 justify-center">
            <Link href="/privacy-policy" className="hover:text-white transition-colors min-h-[44px] flex items-center">Privacy Policy</Link>
            <Link href="/terms-of-service" className="hover:text-white transition-colors min-h-[44px] flex items-center">Terms of Service</Link>
            <a href="#" className="hover:text-white transition-colors min-h-[44px] flex items-center">Cookie Settings</a>
          </div>
        </div>
      </div>
    </footer>
  );
}